import React from 'react';
import { toast } from 'react-toastify';
import { ArcSocketContext } from '@src/core/socket';
import { UseCommandPendingOptions, UseCommandPendingResult } from './types';

const COMMAND_STATUS_TYPE = 'COMMAND_STATUS';
const ACCEPTED_STATUS = 'ACCEPTED';
const FINAL_STATUSES = ['SUCCESS', 'ERROR'];

export const useCommandPending = (options: UseCommandPendingOptions): UseCommandPendingResult => {
  const { command, serialNumber, acceptTimeoutMs, completeTimeoutMs, timeoutMessage } = options;
  const { arcSocket } = React.useContext(ArcSocketContext);

  const [isPending, setIsPending] = React.useState(false);
  const pendingRef = React.useRef(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const setPending = React.useCallback((value: boolean) => {
    pendingRef.current = value;
    setIsPending(value);
  }, []);

  const stopTimeout = React.useCallback(() => {
    if (timeoutRef.current === null) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  const release = React.useCallback(() => {
    stopTimeout();
    setPending(false);
  }, [stopTimeout, setPending]);

  const arm = React.useCallback(
    (ms: number) => {
      stopTimeout();
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        setPending(false);
        toast.warning(timeoutMessage);
      }, ms);
    },
    [stopTimeout, setPending, timeoutMessage]
  );

  const start = React.useCallback(() => {
    setPending(true);
    arm(acceptTimeoutMs);
  }, [setPending, arm, acceptTimeoutMs]);

  React.useEffect(() => {
    const socket = arcSocket();
    if (!socket || !serialNumber) return;

    const onMessage = (event: MessageEvent) => {
      try {
        if (!pendingRef.current) return;

        const data = JSON.parse(event.data ?? '{}');
        if (data?.type !== COMMAND_STATUS_TYPE) return;
        if (data?.command !== command) return;
        if (data?.serialNumber && data.serialNumber !== serialNumber) return;

        const status = String(data?.status ?? '').toUpperCase();

        if (FINAL_STATUSES.includes(status)) {
          release();
          return;
        }

        if (status === ACCEPTED_STATUS) {
          if (completeTimeoutMs === undefined) release();
          else arm(completeTimeoutMs);
        }
      } catch (_) {
        // ignore malformed messages
      }
    };

    socket.addEventListener('message', onMessage);
    return () => {
      socket.removeEventListener('message', onMessage);
    };
  }, [arcSocket, serialNumber, command, completeTimeoutMs, arm, release]);

  React.useEffect(() => () => release(), [serialNumber, release]);

  return { isPending, start, release };
};

export default useCommandPending;
